/**
 * Reportes de Residentes (v3.28.15).
 *
 * MVP: primer reporte = Actas IOMA.
 * Estructura preparada para agregar más reportes en sub-tabs.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { FileSpreadsheet, FileText, AlertTriangle, ShieldPlus, Search, Filter, ArrowLeft, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ALERT_META = {
  missing:      { label: 'Sin ACTA',        cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  expired:      { label: 'ACTA vencida',    cls: 'bg-red-100 text-red-900 border-red-300' },
  expiring_30:  { label: 'Vence ≤ 30 días', cls: 'bg-red-50 text-red-800 border-red-200' },
  expiring_60:  { label: 'Vence ≤ 60 días', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  future:       { label: 'A futuro',        cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  ok:           { label: 'Vigente',         cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
};

const PAMI_ALERT_META = {
  missing: { label: 'Sin Incidencia',    cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  future:  { label: 'Incidencia futura', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  ok:      { label: 'Vigente',           cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
};

function fmtDate(v) {
  if (!v) return '—';
  return String(v).slice(0, 10).split('-').reverse().join('/');
}

function fmtMoney(v) {
  const n = Number(v || 0);
  return n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
}

function AdicCell({ data }) {
  if (!data) return <span className="text-muted-foreground text-[11px]">—</span>;
  return (
    <div className="text-[11px] leading-tight">
      <div className="tabular-nums">{data.qty} × {fmtMoney(data.unit_price)}</div>
      <div className="font-semibold text-emerald-800 tabular-nums">{fmtMoney(data.total)}</div>
    </div>
  );
}

function ActasIomaReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ rows: [], affiliate_types: [], totals: null });

  // Filtros
  const [admFrom, setAdmFrom] = useState('');
  const [admTo, setAdmTo] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [allTypes, setAllTypes] = useState(true);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [alertFilter, setAlertFilter] = useState(new Set()); // vacío = todas

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (admFrom) params.append('admission_from', admFrom);
      if (admTo)   params.append('admission_to', admTo);
      params.append('include_all_types', String(allTypes));
      if (!allTypes && selectedTypes.size > 0) {
        params.append('affiliate_types', Array.from(selectedTypes).join(','));
      }
      if (search.trim()) params.append('search', search.trim());
      params.append('include_inactive', String(includeInactive));
      if (alertFilter.size > 0) params.append('alerts', Array.from(alertFilter).join(','));

      const r = await axios.get(`${BACKEND_URL}/api/residents/reports/actas-ioma?${params.toString()}`,
        { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [admFrom, admTo, allTypes, selectedTypes, search, includeInactive, alertFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleType = (t) => {
    const next = new Set(selectedTypes);
    next.has(t) ? next.delete(t) : next.add(t);
    setSelectedTypes(next);
    if (next.size > 0) setAllTypes(false);
  };

  const toggleAlert = (code) => {
    const next = new Set(alertFilter);
    next.has(code) ? next.delete(code) : next.add(code);
    setAlertFilter(next);
  };

  const counts = data.totals?.counts || {};

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (admFrom) params.append('admission_from', admFrom);
      if (admTo)   params.append('admission_to', admTo);
      params.append('include_all_types', String(allTypes));
      if (!allTypes && selectedTypes.size > 0) {
        params.append('affiliate_types', Array.from(selectedTypes).join(','));
      }
      if (search.trim()) params.append('search', search.trim());
      params.append('include_inactive', String(includeInactive));
      if (alertFilter.size > 0) params.append('alerts', Array.from(alertFilter).join(','));

      window.open(`${BACKEND_URL}/api/residents/reports/actas-ioma/excel?${params.toString()}`, '_blank');
    } catch (e) {
      toast.error('No se pudo exportar');
    }
  };

  return (
    <div className="space-y-4" data-testid="report-actas-ioma">
      {/* Encabezado */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ShieldPlus className="w-5 h-5 text-blue-600" />
            Actas IOMA
          </h2>
          <p className="text-sm text-muted-foreground">
            Residentes con convenio IOMA · vigencia del acta · alertas por vencimiento
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="btn-reload">
            <Filter className="w-4 h-4 mr-1" /> Recargar
          </Button>
          <Button variant="outline" size="sm" onClick={exportExcel} data-testid="btn-export-excel">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Contadores / accesos rápidos por alerta */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {Object.entries(ALERT_META).map(([code, meta]) => {
          const n = counts[code] ?? 0;
          const active = alertFilter.has(code);
          return (
            <button
              key={code}
              onClick={() => toggleAlert(code)}
              className={`rounded-lg border px-3 py-2 text-left hover:shadow transition ${meta.cls} ${active ? 'ring-2 ring-offset-1 ring-blue-500' : ''}`}
              data-testid={`alert-chip-${code}`}
            >
              <div className="text-[10px] uppercase tracking-wide opacity-80">{meta.label}</div>
              <div className="text-2xl font-bold leading-tight">{n}</div>
            </button>
          );
        })}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Fecha ingreso desde</Label>
              <Input type="date" value={admFrom} onChange={(e) => setAdmFrom(e.target.value)} data-testid="fil-adm-from" />
            </div>
            <div>
              <Label className="text-xs">Fecha ingreso hasta</Label>
              <Input type="date" value={admTo} onChange={(e) => setAdmTo(e.target.value)} data-testid="fil-adm-to" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Búsqueda (Apellido / Nombre / DNI / N° Acta)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-3 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" placeholder="Escribí para filtrar..." data-testid="fil-search" />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Label className="text-xs">Tipo de afiliado:</Label>
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <Checkbox checked={allTypes} onCheckedChange={(v) => { setAllTypes(!!v); if (v) setSelectedTypes(new Set()); }} data-testid="fil-all-types" />
              Todos
            </label>
            <div className="flex flex-wrap gap-1">
              {(data.affiliate_types || []).map((t) => (
                <button
                  key={t}
                  onClick={() => toggleType(t)}
                  className={`text-xs px-2 py-1 rounded-full border ${selectedTypes.has(t) ? 'bg-blue-600 text-white border-blue-700' : 'bg-white hover:bg-slate-50'}`}
                  data-testid={`fil-type-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-1 text-xs">
              <Checkbox checked={includeInactive} onCheckedChange={(v) => setIncludeInactive(!!v)} data-testid="fil-inactive" />
              Incluir egresados / archivados
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="pt-4">
          <div className="text-xs text-muted-foreground mb-2">
            Mostrando <b>{data.rows?.length ?? 0}</b> de <b>{data.totals?.residents ?? 0}</b> residentes IOMA
            {alertFilter.size > 0 && <span> · filtro: {Array.from(alertFilter).map(a => ALERT_META[a]?.label).join(', ')}</span>}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Estado</TableHead>
                <TableHead>Apellido y Nombre</TableHead>
                <TableHead>DNI</TableHead>
                <TableHead>Fecha Ingreso</TableHead>
                <TableHead>Fecha Egreso</TableHead>
                <TableHead>Tipo Afiliado</TableHead>
                <TableHead>N° Acta</TableHead>
                <TableHead>Vigencia</TableHead>
                <TableHead>Días</TableHead>
                <TableHead className="text-center">Pañales<br/><span className="text-[10px] font-normal text-muted-foreground">(801)</span></TableHead>
                <TableHead className="text-center">Adic. Complem.<br/><span className="text-[10px] font-normal text-muted-foreground">(400)</span></TableHead>
                <TableHead>PDF</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={13} className="text-center text-muted-foreground">Cargando...</TableCell></TableRow>
              )}
              {!loading && (data.rows?.length ?? 0) === 0 && (
                <TableRow><TableCell colSpan={13} className="text-center text-muted-foreground">Sin resultados con los filtros aplicados</TableCell></TableRow>
              )}
              {!loading && data.rows?.map((r) => {
                const meta = ALERT_META[r.alert.code] || ALERT_META.ok;
                return (
                  <TableRow key={r.resident_id} data-testid={`row-${r.resident_id}`}>
                    <TableCell>
                      <Badge className={`${meta.cls} border`}>{meta.label}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{r.last_name ? `${r.last_name}, ${r.first_name || ''}` : r.full_name}</TableCell>
                    <TableCell className="text-xs">{r.dni}</TableCell>
                    <TableCell className="text-xs">{fmtDate(r.admission_date)}</TableCell>
                    <TableCell className="text-xs">{r.discharge_date ? <span className="text-rose-700">{fmtDate(r.discharge_date)}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-xs">{r.affiliate_type || <span className="text-amber-700">— sin tipo —</span>}</TableCell>
                    <TableCell className="text-xs">{r.acta_number || <span className="text-red-700">FALTA</span>}</TableCell>
                    <TableCell className="text-xs">
                      {r.acta_valid_from || r.acta_valid_until
                        ? <>{fmtDate(r.acta_valid_from)} → {fmtDate(r.acta_valid_until)}</>
                        : <span className="text-red-700">—</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.alert.days_to_expire != null && (
                        <span className={r.alert.days_to_expire < 0 ? 'text-red-700 font-medium' : r.alert.days_to_expire <= 30 ? 'text-red-700' : r.alert.days_to_expire <= 60 ? 'text-amber-700' : ''}>
                          {r.alert.days_to_expire < 0 ? `vencida hace ${-r.alert.days_to_expire}d` : `${r.alert.days_to_expire}d`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center"><AdicCell data={r.adic_801} /></TableCell>
                    <TableCell className="text-center"><AdicCell data={r.adic_400} /></TableCell>
                    <TableCell>
                      {r.acta_attachment_id
                        ? <a href={`${BACKEND_URL}/api/attachments/file/${r.acta_attachment_id}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-1"><Paperclip className="w-3 h-3" />Ver</a>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/residents?open=${r.resident_id}`)} data-testid={`go-${r.resident_id}`}>
                        Abrir
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


export default function ResidentsReports() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [tab, setTab] = useState('actas-ioma');

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="residents-reports">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/residents')} data-testid="btn-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver a Residentes
        </Button>
        <h1 className="text-2xl font-bold">Reportes · Residentes</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="actas-ioma" data-testid="tab-actas-ioma">
            <ShieldPlus className="w-4 h-4 mr-1" /> Actas IOMA
          </TabsTrigger>
          <TabsTrigger value="pami" data-testid="tab-pami">
            <ShieldPlus className="w-4 h-4 mr-1" /> PAMI
          </TabsTrigger>
        </TabsList>
        <TabsContent value="actas-ioma" className="pt-3">
          <ActasIomaReport />
        </TabsContent>
        <TabsContent value="pami" className="pt-3">
          <PamiReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function PamiReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ rows: [], affiliate_types: [], totals: null });
  const [admFrom, setAdmFrom] = useState('');
  const [admTo, setAdmTo] = useState('');
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [alertFilter, setAlertFilter] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (admFrom) params.append('admission_from', admFrom);
      if (admTo)   params.append('admission_to', admTo);
      if (search.trim()) params.append('search', search.trim());
      params.append('include_inactive', String(includeInactive));
      if (alertFilter.size > 0) params.append('alerts', Array.from(alertFilter).join(','));
      const r = await axios.get(`${BACKEND_URL}/api/residents/reports/pami?${params.toString()}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el reporte');
    } finally { setLoading(false); }
  }, [admFrom, admTo, search, includeInactive, alertFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleAlert = (code) => {
    const next = new Set(alertFilter);
    next.has(code) ? next.delete(code) : next.add(code);
    setAlertFilter(next);
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    if (admFrom) params.append('admission_from', admFrom);
    if (admTo)   params.append('admission_to', admTo);
    if (search.trim()) params.append('search', search.trim());
    params.append('include_inactive', String(includeInactive));
    if (alertFilter.size > 0) params.append('alerts', Array.from(alertFilter).join(','));
    window.open(`${BACKEND_URL}/api/residents/reports/pami/excel?${params.toString()}`, '_blank');
  };

  const counts = data.totals?.counts || {};

  return (
    <div className="space-y-4" data-testid="report-pami">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ShieldPlus className="w-5 h-5 text-emerald-700" /> PAMI · Incidencias
          </h2>
          <p className="text-sm text-muted-foreground">
            Residentes con convenio PAMI · fecha de Incidencia · días desde el ingreso
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="btn-reload-pami">
            <Filter className="w-4 h-4 mr-1" /> Recargar
          </Button>
          <Button variant="outline" size="sm" onClick={exportExcel} data-testid="btn-export-pami">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {Object.entries(PAMI_ALERT_META).map(([code, meta]) => {
          const n = counts[code] ?? 0;
          const active = alertFilter.has(code);
          return (
            <button key={code} onClick={() => toggleAlert(code)}
                    className={`rounded-lg border px-3 py-2 text-left hover:shadow ${meta.cls} ${active ? 'ring-2 ring-offset-1 ring-emerald-500' : ''}`}
                    data-testid={`pami-chip-${code}`}>
              <div className="text-[10px] uppercase tracking-wide opacity-80">{meta.label}</div>
              <div className="text-2xl font-bold leading-tight">{n}</div>
            </button>
          );
        })}
      </div>

      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label className="text-xs">Fecha ingreso desde</Label>
              <Input type="date" value={admFrom} onChange={(e) => setAdmFrom(e.target.value)} data-testid="pami-adm-from" /></div>
            <div><Label className="text-xs">Fecha ingreso hasta</Label>
              <Input type="date" value={admTo} onChange={(e) => setAdmTo(e.target.value)} data-testid="pami-adm-to" /></div>
            <div className="md:col-span-2">
              <Label className="text-xs">Búsqueda (Apellido / Nombre / DNI)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-3 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" placeholder="Escribí para filtrar..." data-testid="pami-search" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs justify-end">
            <Checkbox checked={includeInactive} onCheckedChange={(v) => setIncludeInactive(!!v)} data-testid="pami-inactive" />
            Incluir egresados / archivados
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="text-xs text-muted-foreground mb-2">
            Mostrando <b>{data.rows?.length ?? 0}</b> de <b>{data.totals?.residents ?? 0}</b> residentes PAMI
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Estado</TableHead>
              <TableHead>Apellido y Nombre</TableHead>
              <TableHead>DNI</TableHead>
              <TableHead>N° Afiliado</TableHead>
              <TableHead>Fecha Ingreso</TableHead>
              <TableHead>Fecha Egreso</TableHead>
              <TableHead>Incidencia desde</TableHead>
              <TableHead>Días Ingreso→Incidencia</TableHead>
              <TableHead className="text-center">Pañales<br/><span className="text-[10px] font-normal text-muted-foreground">(801)</span></TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground">Cargando...</TableCell></TableRow>}
              {!loading && (data.rows?.length ?? 0) === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground">Sin resultados</TableCell></TableRow>
              )}
              {!loading && data.rows?.map((r) => {
                const meta = PAMI_ALERT_META[r.alert.code] || PAMI_ALERT_META.ok;
                const days = r.days_admission_to_incidence;
                return (
                  <TableRow key={r.resident_id} data-testid={`pami-row-${r.resident_id}`}>
                    <TableCell><Badge className={`${meta.cls} border`}>{meta.label}</Badge></TableCell>
                    <TableCell className="font-medium">{r.last_name ? `${r.last_name}, ${r.first_name || ''}` : r.full_name}</TableCell>
                    <TableCell className="text-xs">{r.dni}</TableCell>
                    <TableCell className="text-xs">{r.affiliate_number || '—'}</TableCell>
                    <TableCell className="text-xs">{fmtDate(r.admission_date)}</TableCell>
                    <TableCell className="text-xs">{r.discharge_date ? <span className="text-rose-700">{fmtDate(r.discharge_date)}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-xs">{r.pami_incidence_from ? fmtDate(r.pami_incidence_from) : <span className="text-red-700">FALTA</span>}</TableCell>
                    <TableCell className="text-xs">
                      {days != null ? (
                        <span className={days < 0 ? 'text-emerald-700' : days > 90 ? 'text-amber-700' : ''}>
                          {days < 0 ? `${-days} d antes` : `${days} d después`}
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-center"><AdicCell data={r.adic_801} /></TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/residents?open=${r.resident_id}`)}>Abrir</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
